import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Download, ExternalLink, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const SIZES = [
  { key: "portrait", label: "Portrait", hint: "4:5 · feed & print" },
  { key: "square", label: "Square", hint: "1:1 · Instagram & LinkedIn" },
  { key: "story", label: "Story", hint: "9:16 · Stories & Reels" },
];

async function copyText(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} caption copied`);
    return true;
  } catch (e) {
    toast.error("Couldn't copy — select the text and copy manually");
    return false;
  }
}

/**
 * Share kit: the three flyer sizes (open / download) + copy-ready captions.
 * `kit` = { portrait|square|story: { url, download_url }, captions: { instagram, linkedin } }
 */
export default function ShareKit({ kit, compact = false }) {
  const [copied, setCopied] = useState(null);
  if (!kit) return null;
  const sizes = SIZES.filter((s) => kit[s.key]?.url);
  const caps = kit.captions || {};
  const doCopy = async (k, label) => { if (await copyText(caps[k] || "", label)) { setCopied(k); setTimeout(() => setCopied(null), 1800); } };
  return (
    <div className="space-y-4" data-testid="share-kit">
      {sizes.length > 0 && (
        <div className={`grid gap-3 ${compact ? "grid-cols-3" : "sm:grid-cols-3"}`} data-testid="share-kit-sizes">
          {sizes.map((s) => (
            <div key={s.key} className="rounded-xl border border-border bg-background p-2" data-testid={`share-kit-${s.key}`}>
              <a href={kit[s.key].url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-center overflow-hidden rounded-lg bg-secondary/40 ${compact ? "h-52" : "h-64"}`} title={`Open ${s.label}`}>
                <img src={kit[s.key].url} alt={`${s.label} flyer`} className="max-h-full max-w-full rounded-md object-contain shadow-sm" loading="lazy" />
              </a>
              <div className="mt-2 flex items-center justify-between gap-1">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground">{s.label}</p>
                  <p className="truncate text-[10px] text-muted-foreground">{s.hint}</p>
                </div>
                <div className="flex shrink-0 items-center">
                  <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Open"><a href={kit[s.key].url} target="_blank" rel="noopener noreferrer" data-testid={`share-kit-open-${s.key}`}><ExternalLink className="h-3.5 w-3.5" /></a></Button>
                  <Button asChild variant="ghost" size="icon" className="h-7 w-7" title="Download PNG"><a href={kit[s.key].download_url || kit[s.key].url} data-testid={`share-kit-download-${s.key}`}><Download className="h-3.5 w-3.5" /></a></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {(caps.instagram || caps.linkedin) && (
        <Tabs defaultValue="instagram" data-testid="share-kit-captions">
          <div className="flex items-center justify-between gap-2">
            <TabsList className="h-8">
              <TabsTrigger value="instagram" className="h-7 text-xs" data-testid="share-kit-tab-instagram"><Instagram className="mr-1.5 h-3.5 w-3.5" /> Instagram</TabsTrigger>
              <TabsTrigger value="linkedin" className="h-7 text-xs" data-testid="share-kit-tab-linkedin"><Linkedin className="mr-1.5 h-3.5 w-3.5" /> LinkedIn</TabsTrigger>
            </TabsList>
          </div>
          {["instagram", "linkedin"].map((k) => (
            <TabsContent key={k} value={k} className="mt-2">
              <div className="relative">
                <textarea readOnly value={caps[k] || ""} rows={compact ? 7 : 9} className="w-full resize-y rounded-lg border border-border bg-background p-3 pr-28 text-xs leading-relaxed text-foreground" data-testid={`share-kit-caption-${k}`} />
                <Button type="button" size="sm" className="absolute right-2 top-2 h-7" onClick={() => doCopy(k, k === "instagram" ? "Instagram" : "LinkedIn")} data-testid={`share-kit-copy-${k}`}>
                  {copied === k ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}{copied === k ? "Copied" : "Copy caption"}
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
